import React, { useState, useEffect, useCallback } from 'react';
import { homepageAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import './HeroImageAdmin.css';

function HeroImageAdmin() {
  const [heroImages, setHeroImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({
    imageUrl: '',
    altText: '',
    caption: '',
    displayOrder: 1,
    active: true
  });

  const loadHeroImages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await homepageAPI.getHeroImages();
      
      if (response.success && response.data) {
        setHeroImages(response.data);
      } else {
        throw new Error('Failed to load hero images');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading hero images:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let ignore = false;
    // On mount `loading` already starts true and `error` null, so drive the
    // fetch directly here instead of calling loadHeroImages() synchronously
    // (which would set state synchronously inside the effect).
    ;(async () => {
      try {
        const response = await homepageAPI.getHeroImages();
        if (ignore) return;
        if (response.success && response.data) {
          setHeroImages(response.data);
        } else {
          throw new Error('Failed to load hero images');
        }
      } catch (err) {
        if (ignore) return;
        setError(err.message);
        console.error('Error loading hero images:', err);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError(null);
      
      if (editingImage) {
        const response = await homepageAPI.updateHeroImage(editingImage.imageId, formData);
        if (response.success) {
          await loadHeroImages();
          setEditingImage(null);
          resetForm();
        }
      } else {
        const response = await homepageAPI.addHeroImage(formData);
        if (response.success) {
          await loadHeroImages();
          setShowAddForm(false);
          resetForm();
        }
      }
    } catch (err) {
      setError(err.message);
      console.error('Error saving hero image:', err);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this hero image?')) {
      return;
    }
    
    try {
      setError(null);
      const response = await homepageAPI.deleteHeroImage(imageId);
      
      if (response.success) {
        await loadHeroImages();
      }
    } catch (err) {
      setError(err.message);
      console.error('Error deleting hero image:', err);
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      imageUrl: image.imageUrl || '',
      altText: image.altText || '',
      caption: image.caption || '',
      displayOrder: image.displayOrder || 1,
      active: image.active !== false
    });
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      imageUrl: '',
      altText: '',
      caption: '',
      displayOrder: 1,
      active: true
    });
    setShowAddForm(false);
    setEditingImage(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return <LoadingSpinner message="Loading hero images..." />;
  }

  return (
    <div className="hero-image-admin">
      <div className="admin-header">
        <h2>Hero Banner Images</h2>
        <button 
          className="btn-primary"
          onClick={() => setShowAddForm(true)}
          disabled={showAddForm}
        >
          Add New Image
        </button>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {showAddForm && (
        <div className="hero-form-container">
          <form onSubmit={handleSubmit} className="hero-form">
            <h3>{editingImage ? 'Edit Hero Image' : 'Add New Hero Image'}</h3>
            
            <div className="form-group">
              <label htmlFor="imageUrl">Image URL *</label>
              <input
                type="url"
                id="imageUrl"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleInputChange}
                required
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div className="form-group">
              <label htmlFor="altText">Alt Text</label>
              <input
                type="text"
                id="altText"
                name="altText"
                value={formData.altText}
                onChange={handleInputChange}
                placeholder="Descriptive text for accessibility"
              />
            </div>

            <div className="form-group">
              <label htmlFor="caption">Caption</label>
              <input
                type="text"
                id="caption"
                name="caption"
                value={formData.caption}
                onChange={handleInputChange}
                placeholder="Optional caption to display on image"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="displayOrder">Display Order</label>
                <input
                  type="number"
                  id="displayOrder"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleInputChange}
                  min="1"
                  max="999"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingImage ? 'Update Image' : 'Add Image'}
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="hero-images-list">
        {heroImages.length === 0 ? (
          <div className="no-images">
            <p>No hero images configured. Add your first image to get started.</p>
          </div>
        ) : (
          <div className="images-grid">
            {heroImages.map((image) => (
              <div key={image.imageId} className="image-card">
                <div className="image-preview">
                  <img 
                    src={image.imageUrl} 
                    alt={image.altText || 'Hero image'} 
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div className="image-error" style={{ display: 'none' }}>
                    Image failed to load
                  </div>
                </div>
                
                <div className="image-info">
                  <div className="image-meta">
                    <span className={`status ${image.active ? 'active' : 'inactive'}`}>
                      {image.active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="order">Order: {image.displayOrder}</span>
                  </div>
                  
                  {image.caption && (
                    <p className="image-caption">{image.caption}</p>
                  )}
                  
                  {image.altText && (
                    <p className="image-alt">Alt: {image.altText}</p>
                  )}
                  
                  <div className="image-actions">
                    <button 
                      className="btn-edit"
                      onClick={() => handleEdit(image)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDelete(image.imageId)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default HeroImageAdmin;